import {
  Checkbox,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { MdCheck, MdExpandMore, MdInfo } from '@teamworksdev/icons';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AthleteSelect } from '../../../../components/layout/athlete-select';
import { cn } from '../../../../components/utils';
import { useFlags } from '../../../../context/featureFlag';
import { useViewer } from '../../../../context/viewer';
import { useStorage } from '../../../../hooks/useStorage';
import { usePlanContext } from '../../context/PlanContext';
import { getTeamColor } from '../../timeline/components/cross-team-colors';
import { getEffectiveTrackColor } from '../../timeline/utils';
import { useCalendarContext } from '../context/CalendarContext';
import {
  deriveHubEventTypesFromEvents,
  useCalendarEvents,
} from '../hooks/useCalendarEvents';

export function CalendarFilter() {
  const { t } = useTranslation();
  const {
    palette: {
      products: { performance },
      text: { secondary },
    },
  } = useTheme();
  const {
    athletes,
    tracks,
    filteredTracks,
    selectedAthlete,
    setSelectedAthlete,
    showAdditionalTeams,
  } = usePlanContext();
  const { hubConnected } = useViewer();
  const { hubEventsOnCalendarFlag } = useFlags();
  const {
    visibleRange,
    selectedTrackIds,
    toggleTrackSelection,
    selectAllTracks,
    unselectAllTracks,
    selectedCrossTeamTrackIds,
    toggleCrossTeamTrackSelection,
    selectAllCrossTeamTracks,
    unselectAllCrossTeamTracks,
    additionalTeamsData,
    allCrossTeamTrackIds,
    deselectedHubEventTypeIds,
    toggleHubEventTypeSelection,
    selectAllHubEventTypes,
    unselectAllHubEventTypes,
  } = useCalendarContext();
  // Sections start collapsed so the whole filter stack — Additional Teams
  // included — is reachable on first load without scrolling. The shared
  // container below scrolls as a single unit once a section is expanded.
  // useStorage keys the choice per user so it survives reloads.
  const [hubEventsExpanded, setHubEventsExpanded] = useStorage(
    'calendar-filter:hub-events-expanded',
    false,
  );
  const [tracksExpanded, setTracksExpanded] = useStorage(
    'calendar-filter:tracks-expanded',
    false,
  );
  const [additionalTeamsExpanded, setAdditionalTeamsExpanded] = useStorage(
    'calendar-filter:additional-teams-expanded',
    false,
  );

  const tracksById = useMemo(
    () => new Map(tracks.map((t) => [t.id, t])),
    [tracks],
  );

  const { data: hubData } = useCalendarEvents({
    startDate: visibleRange?.start ?? null,
    endDate: visibleRange?.end ?? null,
  });
  const hubEventTypes = useMemo(() => {
    const fromApi = hubData?.eventTypes ?? [];
    if (fromApi.length > 0) {
      return fromApi;
    }
    return deriveHubEventTypesFromEvents(
      (hubData?.events ?? []) as Parameters<
        typeof deriveHubEventTypesFromEvents
      >[0],
    );
  }, [hubData?.eventTypes, hubData?.events]);
  const showHubEventsSection =
    hubConnected && hubEventsOnCalendarFlag && hubEventTypes.length > 0;

  const allHubEventTypesSelected =
    hubEventTypes.length > 0 &&
    hubEventTypes.every(
      (eventType) => !deselectedHubEventTypeIds.has(eventType.id),
    );
  const allTracksSelected =
    filteredTracks.length > 0 &&
    filteredTracks.every((track) => selectedTrackIds.includes(track.id));

  const showAdditionalTeamsSection =
    showAdditionalTeams && additionalTeamsData.length > 0;
  const allCrossTeamTracksSelected =
    allCrossTeamTrackIds.length > 0 &&
    allCrossTeamTrackIds.every((id) => selectedCrossTeamTrackIds.includes(id));

  const filteredAthletes = useMemo(() => {
    const selectedTrackSet = new Set(selectedTrackIds);
    const athleteIdsInSelectedTracks = new Set<string>();

    for (const track of tracks) {
      if (selectedTrackSet.has(track.id)) {
        for (const athlete of track.athletes) {
          athleteIdsInSelectedTracks.add(athlete.profileId);
        }
      }
    }

    return athletes.filter((a) => athleteIdsInSelectedTracks.has(a.id));
  }, [tracks, selectedTrackIds, athletes]);

  return (
    <div className="flex flex-col gap-2 w-full h-full min-h-0">
      <div className="shrink-0">
        <Typography variant="body1Medium">{t('calendar')}</Typography>
      </div>

      <div className="shrink-0">
        <AthleteSelect
          athletes={filteredAthletes}
          selectedAthlete={selectedAthlete}
          onChange={setSelectedAthlete}
          fullWidth
        />
      </div>

      {/* One scroll area for the whole section stack, so an expanded section
          never squeezes the others and every option stays reachable. */}
      <div className="flex flex-col flex-1 min-h-0 gap-4 pt-2 overflow-y-auto">
        {showHubEventsSection && (
          <CollapsibleFilterSection
            title={t('hubEvents')}
            expanded={hubEventsExpanded}
            onExpandedChange={setHubEventsExpanded}
            titleAdornment={
              <Tooltip
                title={
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold leading-4 text-white">
                      {t('hubEventsTooltipTitle')}
                    </span>
                    <span className="text-xs font-normal leading-4 text-white/60">
                      {t('hubEventsTooltipDescription')}
                    </span>
                  </div>
                }
                placement="bottom-end"
                arrow
              >
                <span className="flex items-center cursor-default" tabIndex={0}>
                  <MdInfo size={16} aria-hidden="true" />
                </span>
              </Tooltip>
            }
          >
            <SelectAllCheckboxRow
              allSelected={allHubEventTypesSelected}
              onSelectAll={selectAllHubEventTypes}
              onUnselectAll={() =>
                unselectAllHubEventTypes(
                  hubEventTypes.map((eventType) => eventType.id),
                )
              }
              checkColor={secondary}
            />
            {hubEventTypes.map((eventType) => (
              <FilterCheckboxItem
                key={eventType.id}
                label={eventType.name}
                isChecked={!deselectedHubEventTypeIds.has(eventType.id)}
                onToggle={() => toggleHubEventTypeSelection(eventType.id)}
                checkColor={secondary}
              />
            ))}
          </CollapsibleFilterSection>
        )}

        {showHubEventsSection && <Divider className="shrink-0" />}

        <CollapsibleFilterSection
          title={t('tracks')}
          expanded={tracksExpanded}
          onExpandedChange={setTracksExpanded}
        >
          <SelectAllCheckboxRow
            allSelected={allTracksSelected}
            onSelectAll={selectAllTracks}
            onUnselectAll={unselectAllTracks}
            checkColor={performance.primary}
          />
          {filteredTracks.map((track) => (
            <FilterCheckboxItem
              key={track.id}
              label={track.name}
              isChecked={selectedTrackIds.includes(track.id)}
              onToggle={() => toggleTrackSelection(track.id)}
              checkColor={
                getEffectiveTrackColor(track, tracksById) ?? performance.primary
              }
              className={track.parentTrackId ? 'pl-6' : undefined}
            />
          ))}
        </CollapsibleFilterSection>

        {showAdditionalTeamsSection && <Divider className="shrink-0" />}

        {showAdditionalTeamsSection && (
          <CollapsibleFilterSection
            title={t('additionalTeams', {
              teamCount: additionalTeamsData.length,
            })}
            expanded={additionalTeamsExpanded}
            onExpandedChange={setAdditionalTeamsExpanded}
          >
            <SelectAllCheckboxRow
              allSelected={allCrossTeamTracksSelected}
              onSelectAll={selectAllCrossTeamTracks}
              onUnselectAll={unselectAllCrossTeamTracks}
              checkColor={performance.primary}
            />
            {additionalTeamsData.map((team, index) => (
              <div key={team.teamId} className="flex flex-col">
                <Typography
                  variant="caption"
                  className="pl-1 pt-1 font-semibold"
                  color="text.secondary"
                >
                  {team.teamName}
                </Typography>
                {team.tracks.map((track) => (
                  <FilterCheckboxItem
                    key={track.id}
                    label={track.name}
                    isChecked={selectedCrossTeamTrackIds.includes(track.id)}
                    onToggle={() => toggleCrossTeamTrackSelection(track.id)}
                    checkColor={getTeamColor(index)}
                    className={track.parentTrackId ? 'pl-6' : undefined}
                  />
                ))}
              </div>
            ))}
          </CollapsibleFilterSection>
        )}
      </div>
    </div>
  );
}

type CollapsibleFilterSectionProps = {
  title: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  children: ReactNode;
  titleAdornment?: ReactNode;
};

function CollapsibleFilterSection({
  title,
  expanded,
  onExpandedChange,
  children,
  titleAdornment,
}: CollapsibleFilterSectionProps) {
  return (
    // shrink-0 keeps an expanded section at its natural height instead of
    // competing with its siblings for the panel's flex space (which used to
    // squash every open section down to a row or two).
    <div className="flex flex-col shrink-0">
      <div className="flex items-center justify-between shrink-0 w-full">
        <div className="flex items-center gap-1">
          <Typography variant="body1Medium">{title}</Typography>
          {titleAdornment}
        </div>
        <IconButton
          size="small"
          onClick={() => onExpandedChange(!expanded)}
          aria-expanded={expanded}
          aria-label={title}
        >
          <MdExpandMore
            size={20}
            className={cn('transition-transform', expanded && 'rotate-180')}
            aria-hidden="true"
          />
        </IconButton>
      </div>
      {expanded && (
        // Natural height, deliberately uncapped: a fixed cap cannot know how
        // much room the panel has, so it clips lists even when there is space
        // to spare. The stack container below owns the scrollbar instead, and
        // only shows one when the sections genuinely exceed the panel.
        <div className="flex flex-col mt-2">{children}</div>
      )}
    </div>
  );
}

type SelectAllCheckboxRowProps = {
  allSelected: boolean;
  onSelectAll: () => void;
  onUnselectAll: () => void;
  checkColor: string;
};

function SelectAllCheckboxRow({
  allSelected,
  onSelectAll,
  onUnselectAll,
  checkColor,
}: SelectAllCheckboxRowProps) {
  const { t } = useTranslation();

  return (
    <FilterCheckboxItem
      label={allSelected ? t('unselectAll') : t('selectAll')}
      isChecked={allSelected}
      onToggle={() => {
        if (allSelected) {
          onUnselectAll();
        } else {
          onSelectAll();
        }
      }}
      checkColor={checkColor}
    />
  );
}

interface FilterCheckboxItemProps {
  label: string;
  isChecked: boolean;
  onToggle: () => void;
  checkColor: string;
  className?: string;
}

function FilterCheckboxItem({
  label,
  isChecked,
  onToggle,
  checkColor,
  className,
}: FilterCheckboxItemProps) {
  return (
    <div
      className={`flex items-center p-1 rounded cursor-pointer hover:bg-gray-50 ${
        className ?? ''
      }`}
      onClick={onToggle}
    >
      <Checkbox
        checked={isChecked}
        size="small"
        className="p-0 mr-2"
        icon={
          <div className="w-4 h-4 border border-gray-300 rounded-sm bg-white" />
        }
        checkedIcon={
          <div
            className="w-4 h-4 rounded-sm flex items-center justify-center"
            style={{ backgroundColor: checkColor }}
          >
            <MdCheck size={12} color="white" aria-hidden="true" />
          </div>
        }
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
      />
      <Typography variant="body2" color="text.primary">
        {label}
      </Typography>
    </div>
  );
}
